/**
 * Shared formatting utilities for consistent display across the application.
 */

/**
 * Parse a date value into a Date object. Date-only strings (`YYYY-MM-DD`) are
 * parsed in local time — `new Date('2025-01-15')` treats them as UTC midnight,
 * which renders as the previous day for users in negative-offset time zones.
 */
function toDate(date: string | Date): Date {
  if (date instanceof Date) return date;
  const dateOnlyMatch = date.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }
  return new Date(date);
}

/**
 * Format a date string into a localized short date.
 *
 * @param date - ISO date string or Date object
 * @returns Formatted date string (e.g. "Jan 15, 2025") or empty string if invalid
 */
export function formatDate(date: string | Date | null | undefined): string {
  if (!date) return '';
  const d = toDate(date);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

/**
 * Format a date string into a localized short date with time.
 *
 * @param date - ISO date string or Date object
 * @returns Object with `date` and `time` strings, or empty strings if invalid
 */
export function formatDateTime(date: string | Date | null | undefined): { date: string; time: string } {
  if (!date) return { date: '', time: '' };
  const d = toDate(date);
  if (isNaN(d.getTime())) return { date: '', time: '' };
  return {
    date: d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }),
    time: d.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    })
  };
}

/**
 * Format a number as USD currency.
 *
 * @param amount - Number or string amount
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted currency string (e.g. "$12.50")
 */
export function formatCurrency(amount: number | string | null | undefined, decimals: number = 2): string {
  if (amount === null || amount === undefined) return 'N/A';
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (isNaN(num)) return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(num);
}

/**
 * Format a duration in seconds to a human-readable string.
 *
 * @param seconds - Duration in seconds
 * @returns Formatted string (e.g. "2m 30s" or "1h 5m 10s")
 */
export function formatDuration(seconds: number | null | undefined): string {
  if (!seconds || seconds <= 0) return '0s';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  const parts: string[] = [];
  if (h > 0) parts.push(`${h}h`);
  if (m > 0) parts.push(`${m}m`);
  if (s > 0 || parts.length === 0) parts.push(`${s}s`);
  return parts.join(' ');
}

/**
 * Format a date as relative time: "just now", "5m ago", "3h ago",
 * "2d ago", "3w ago", "2mo ago", "1y ago".
 *
 * @param date - ISO date string or Date object
 * @returns Short relative time string, or empty string if invalid
 */
export function formatRelativeTime(date: string | Date | null | undefined): string {
  if (!date) return '';
  const d = toDate(date);
  if (isNaN(d.getTime())) return '';
  const now = Date.now();
  const diffMs = now - d.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return 'just now';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  const diffWk = Math.floor(diffDay / 7);
  if (diffWk < 5) return `${diffWk}w ago`;
  const diffMo = Math.floor(diffDay / 30);
  if (diffMo < 12) return `${diffMo}mo ago`;
  const diffYr = Math.floor(diffDay / 365);
  return `${diffYr}y ago`;
}
